import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Typography } from '@mui/material';
import { useEffect, useRef, type ChangeEvent, type ClipboardEvent, type FocusEvent, type KeyboardEvent } from 'react';

export type JourneyOtpStatus = 'idle' | 'error' | 'success';

export const JOURNEY_OTP_LENGTH = 6;

type JourneyOtpInputProps = {
  /** Dígitos já digitados (0 a 6), sem espaços. */
  value: string;
  onChange: (value: string) => void;
  status?: JourneyOtpStatus;
  /** Mensagem abaixo das caixas (ex.: «Código incorreto.»); só aparece em `error` e `success`. */
  message?: string;
  disabled?: boolean;
  /** Foca a primeira caixa ao montar (etapa do código recém-aberta). */
  focusOnMount?: boolean;
  appId: string;
};

const onlyDigits = (raw: string) => raw.replace(/\D/g, '');

/**
 * Código de verificação em 6 caixas (Figma «E-commerce — 2026», nodes 2086:6915 digitando,
 * 2086:6812 incorreto e 2086:7225 verificado). Foco avança a cada dígito, Backspace em caixa
 * vazia volta para a anterior e colar preenche as caixas a partir da atual.
 */
export default function JourneyOtpInput({
  value,
  onChange,
  status = 'idle',
  message,
  disabled = false,
  focusOnMount = false,
  appId
}: JourneyOtpInputProps) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = onlyDigits(value).slice(0, JOURNEY_OTP_LENGTH);
  const messageId = `${appId}-message`;
  const showMessage = Boolean(message) && status !== 'idle';

  useEffect(() => {
    if (focusOnMount) inputs.current[0]?.focus();
  }, [focusOnMount]);

  const focusBox = (index: number) => {
    const target = inputs.current[Math.max(0, Math.min(index, JOURNEY_OTP_LENGTH - 1))];
    target?.focus();
    target?.select();
  };

  /** Escreve `incoming` a partir da caixa `index`, mantendo as anteriores. */
  const fillFrom = (index: number, incoming: string) => {
    const next = (digits.slice(0, index) + incoming).slice(0, JOURNEY_OTP_LENGTH);
    onChange(next);
    focusBox(next.length);
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const incoming = onlyDigits(event.target.value);
    if (!incoming) return;
    // Uma tecla traz 1 dígito; o autofill do SO traz o código inteiro.
    fillFrom(Math.min(index, digits.length), incoming.length > 1 ? incoming : incoming.slice(-1));
  };

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const incoming = onlyDigits(event.clipboardData.getData('text'));
    if (incoming) fillFrom(Math.min(index, digits.length), incoming);
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        onChange(digits.slice(0, index) + digits.slice(index + 1));
        return;
      }
      if (index > 0) {
        onChange(digits.slice(0, index - 1) + digits.slice(index));
        focusBox(index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const borderColor = status === 'error' ? 'error.dark' : status === 'success' ? 'success.main' : 'text.primary';

  return (
    <Box
      data-app-id={appId}
      data-status={status}
      sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}
    >
      {/* No celular as caixas encolhem para caber no cartão; a partir de `sm` ficam no tamanho do Figma (42×50). */}
      <Box
        sx={{
          display: 'flex',
          gap: { sm: '16px', xs: '6px' },
          justifyContent: 'center',
          maxWidth: 348,
          px: { sm: '8px', xs: 0 },
          width: '100%'
        }}
      >
        {Array.from({ length: JOURNEY_OTP_LENGTH }, (_, index) => (
          <Box
            aria-describedby={showMessage ? messageId : undefined}
            aria-invalid={status === 'error'}
            aria-label={`Dígito ${index + 1} de ${JOURNEY_OTP_LENGTH}`}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            component="input"
            data-app-id={`${appId}-digit-${index + 1}`}
            disabled={disabled}
            inputMode="numeric"
            key={index}
            maxLength={1}
            onChange={handleChange(index)}
            onFocus={(event: FocusEvent<HTMLInputElement>) => event.target.select()}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            pattern="[0-9]*"
            ref={(element: HTMLInputElement | null) => {
              inputs.current[index] = element;
            }}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor,
              borderRadius: '8px',
              boxSizing: 'border-box',
              color: 'text.primary',
              fontFamily: 'inherit',
              fontSize: '18px',
              flex: '1 1 0',
              fontWeight: 500,
              height: { sm: '50px', xs: '46px' },
              lineHeight: '28px',
              maxWidth: '42px',
              minWidth: 0,
              outline: 'none',
              p: 0,
              textAlign: 'center',
              transition: 'border-color 120ms ease',
              '&:focus-visible, &:focus': {
                borderColor: status === 'idle' ? 'primary.main' : borderColor,
                boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}33`
              },
              '&:disabled': { color: 'text.disabled', borderColor: 'divider', cursor: 'not-allowed' }
            }}
            type="text"
            value={digits[index] ?? ''}
          />
        ))}
      </Box>

      {showMessage ? (
        <Box
          data-app-id={`${appId}-message`}
          data-status={status}
          id={messageId}
          role={status === 'error' ? 'alert' : 'status'}
          sx={{ alignItems: 'center', alignSelf: 'stretch', display: 'flex' }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', height: 40, justifyContent: 'center', width: 40 }}>
            {status === 'error' ? (
              <ErrorOutlineIcon aria-hidden sx={{ color: 'error.dark', fontSize: 24 }} />
            ) : (
              <CheckCircleOutlineIcon aria-hidden sx={{ color: 'success.dark', fontSize: 24 }} />
            )}
          </Box>
          <Typography sx={{ color: status === 'error' ? 'error.dark' : 'success.dark' }} variant="body1">
            {message}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
